import moment, { Moment } from "moment";
import { ByWeekday, Frequency, Options, RRule, RRuleSet, Weekday, WeekdayStr, rrulestr } from "rrule";

export type RecurrenceUnit =
  | "minute"
  | "hour"
  | "day"
  | "week"
  | "month"
  | "year";

export interface Schedule {
  dueDate?: string;
  isTimeValid?: boolean;
  isRecurring?: boolean;
  recurrenceQuantity?: number;
  recurenceUnit?: RecurrenceUnit;
  cancelIfReceived?: boolean;
  rruleset?: string;
  calendarMessage?: boolean;
}

export type CherryPickOptions = Pick<Options, "freq" | "dtstart" | "interval" | "wkst" | "count" | "until" | "byweekday" | "bymonthday">;
type CherryPickKeys = keyof CherryPickOptions;
const allowedKeys: CherryPickKeys[] = ["freq", "dtstart", "interval", "wkst", "count", "until", "byweekday", "bymonthday"];


const weekdaystr = (m: moment.Moment) => Weekday.fromStr(
  m.locale('en').format("dd").toUpperCase() as WeekdayStr
)

export const weekStart = weekdaystr(moment().day(0));
export const currentDay = weekdaystr(moment());

export const extractSignificantProps = (rule: RRule) => {
  return {
    freq: rule.origOptions.freq,
    bymonthday: Array.isArray(rule.options.bymonthday)
      ? rule.options.bymonthday
      : [rule.options.bymonthday].filter(Boolean),
    byweekday: Array.isArray(rule.options.byweekday)
      ? rule.options.byweekday
      : [rule.options.byweekday].filter(Boolean),
  };
};

export function getByWeekDay(dueDate: Moment) {
  const dayOfWeek = dueDate.day();  // 0 (Sunday) to 6 (Saturday)
  const nthWeek = Math.ceil(dueDate.date() / 7);  // Which occurrence of the day in the month

  switch (dayOfWeek) {
    case 0: return RRule.SU.nth(nthWeek);
    case 1: return RRule.MO.nth(nthWeek);
    case 2: return RRule.TU.nth(nthWeek);
    case 3: return RRule.WE.nth(nthWeek);
    case 4: return RRule.TH.nth(nthWeek);
    case 5: return RRule.FR.nth(nthWeek);
    case 6: return RRule.SA.nth(nthWeek);
    default: throw new Error("Invalid day of the week.");
  }
}

const cherryPick = (rule: RRule) => Object.keys(rule.origOptions).filter(key => allowedKeys.includes(key as CherryPickKeys)).reduce((obj: any, key: any) => {
  const safeKey = key as keyof Options;
  obj[safeKey] = rule.origOptions[safeKey];
  return obj;
}, {} as CherryPickOptions);



export const ruleOptionsFromString = (rfcString: string): CherryPickOptions => cherryPick(RRule.fromString(rfcString))

export const defaultRuleOptions = (freq = RRule.WEEKLY, interval = 1) => ({
  freq: freq,
  interval: interval,
  byweekday: [currentDay.weekday],
  wkst: weekStart,
} as CherryPickOptions)

export const getWeekDay = (m: moment.Moment) => [Weekday.fromStr(
  m.locale('en').format("dd").toUpperCase() as WeekdayStr
).weekday]

export const initialRuleOptions = (m: moment.Moment) =>
  ({ ...defaultRuleOptions(), byweekday: getWeekDay(m) } as Partial<Options>)


export const toRuleSet = (rfcString: string) => rrulestr(rfcString, {
  forceset: true,
}) as RRuleSet;

export const fetchRule = (rfcString: string): RRule => toRuleSet(rfcString)._rrule[0];

export const extractWeekdayNumbers = (weekdays: ByWeekday | ByWeekday[]): number[] => {
  // If it's just a single value, wrap it in an array for consistent processing
  const arrayWeekdays = Array.isArray(weekdays) ? weekdays : [weekdays];

  return arrayWeekdays.map(day => {
    if (day instanceof Weekday) {
      return day.weekday;
    }
    return day as number;
  });
};

// Helper function to determine the closest occurrence either the next or the previous
export function getClosestOccurrence(currentDate: Moment, nextOccurrence: Moment | null, prevOccurrence: Moment | null): moment.Moment | null {
  if (nextOccurrence && prevOccurrence) {
    const nextDiff = Math.abs(nextOccurrence.diff(currentDate));
    const prevDiff = Math.abs(prevOccurrence.diff(currentDate));
    return nextDiff < prevDiff ? nextOccurrence : prevOccurrence;
  } else if (nextOccurrence) {
    return nextOccurrence;
  } else if (prevOccurrence) {
    return prevOccurrence;
  }
  return null;
}

export const patchRuleSet = (set: RRuleSet, patch: Partial<CherryPickOptions>) => {
  const { dtstart, _rrule } = set;
  const newSet = new RRuleSet();
  newSet.dtstart = dtstart;
  set.exdates().forEach(x => newSet.exdate(x));

  _rrule.forEach(r => {
    newSet.rrule(new RRule({ ...ruleOptionsFromString(RRule.optionsToString(r.origOptions)), ...patch }))
  })
  return newSet
}
export const getRuleValue = (set: RRuleSet, key: CherryPickKeys) => set._rrule[0].origOptions[key];

// ── EXDATE helpers (#592b — per-date recurring-drip skip) ────────────────────
//
// A per-date "skip" excludes ONE occurrence from a recurring schedule without
// terminating the series, by adding an EXDATE line to the rruleset. Comparison
// is at MINUTE granularity to match the FE calendar filter
// (recurring-occurrence-filter.ts `excludedMinuteValues`), so a skipped chip
// and a skipped BE occurrence agree.
//
// NOTE: the RRULE is preserved VERBATIM (the parsed RRule is reused) — do NOT
// route it through `patchRuleSet`/`ruleOptionsFromString`, whose cherry-pick
// drops `BYHOUR`/`BYMINUTE` (real drip rrulesets carry the send time there,
// e.g. `FREQ=MONTHLY;BYHOUR=7;BYMINUTE=10`).

const toExdateMinute = (d: Date): number => moment.utc(d).startOf("minute").valueOf();

/** All EXDATE instants currently on the rruleset. */
export const getExdates = (rruleset: string): Date[] => toRuleSet(rruleset).exdates();

/** True when `date` (minute granularity, UTC) is already excluded. */
export const isExdated = (rruleset: string, date: Date): boolean => {
  const target = toExdateMinute(date);
  return toRuleSet(rruleset).exdates().some((ex) => toExdateMinute(ex) === target);
};

/**
 * Set-union `date` into the rruleset's EXDATE lines and return the re-serialized
 * rruleset string. Idempotent: re-adding an already-excluded date (minute match)
 * yields an equivalent string and never duplicates the EXDATE. Preserves DTSTART
 * and the full RRULE (BYHOUR/BYMINUTE included).
 */
export const addExdateToRruleset = (rruleset: string, date: Date): string => {
  const set = toRuleSet(rruleset);
  const existing = set.exdates();
  const newSet = new RRuleSet();
  if (set.dtstart) newSet.dtstart = set.dtstart;
  // Reuse the parsed RRule objects verbatim (keeps BYHOUR/BYMINUTE etc.).
  set._rrule.forEach((r) => newSet.rrule(r));
  existing.forEach((ex) => newSet.exdate(ex));
  if (!existing.some((ex) => toExdateMinute(ex) === toExdateMinute(date))) {
    newSet.exdate(date);
  }
  return newSet.toString();
};

export const parseOldUnits = (recurenceUnit: RecurrenceUnit, recurrenceQuantity: number): RRuleSet => {
  const ruleset = new RRuleSet();
  ruleset.rrule(
    new RRule(
      defaultRuleOptions(
        parseRecurrenceUnit(recurenceUnit),
        recurrenceQuantity
      )
    )
  );
  return ruleset;
}

export const getDefaultOptions = (recurenceUnit: RecurrenceUnit | undefined, recurrenceQuantity: number | undefined) =>
  defaultRuleOptions(parseRecurrenceUnit(recurenceUnit), recurrenceQuantity)

export const optionsFromRuleset = (schedule: Schedule) => {
  let options;
  if (!!schedule.rruleset) {
    const ruleRfc = toRuleSet(schedule.rruleset)._rrule[0].toString();
    options = ruleOptionsFromString(ruleRfc);

    const { byweekday, freq } = options;
    if (freq === Frequency.WEEKLY) {
      const bwd = (byweekday as Weekday[]).map((d) => d.weekday);
      options = { ...options, byweekday: bwd };
    }
  } else {
    options = getDefaultOptions(
      schedule.recurenceUnit,
      schedule.recurrenceQuantity
    )
  }
  return options;
}

export const parseRecurrenceUnit = (unit?: RecurrenceUnit): Frequency => {
  switch (unit) {
    case "day":
      return Frequency.DAILY;
    case "hour":
      return Frequency.HOURLY;
    case "minute":
      return Frequency.MINUTELY;
    case "month":
      return Frequency.MONTHLY;
    case "week":
      return Frequency.WEEKLY;
    case "year":
      return Frequency.YEARLY;
    default:
      return Frequency.DAILY;
  }
};

export const weekOfMonth = (m: moment.Moment): number | undefined => {
  const { months } = m.toObject();
  if (months !== moment(m).add(7, "d").toObject().months) return -1
  if (months !== moment(m).subtract(7, "d").toObject().months) return 1
  if (months !== moment(m).subtract(14, "d").toObject().months) return 2
  if (months !== moment(m).subtract(21, "d").toObject().months) return 3
  if (months !== moment(m).subtract(28, "d").toObject().months) return 4
};

